/**
 * Everything the module needs to know about "how does THIS environment behave", split by
 * VERIFICATION CLASS so each claim's home in the validation harness is explicit:
 *   - existence → feature-detected from `env`; validated by the MDN BCD tripwire test.
 *   - version   → not detectable without side effects; a small table; watched by telemetry.
 *   - behaviour → NOT modelled here. It must be probed by CALLING the API — see `probes.ts`.
 *     The canonical example is `permissions.query` for camera/microphone, which EXISTS on the
 *     Firefox that broke us but THROWS. Existence detection cannot catch that; do not add a
 *     "permissionsQueryUsable" boolean here.
 */
export type Capabilities = {
  /** existence: `getUserMedia` is available (secure context, modern browser). */
  mediaCaptureSupported: boolean;
  /** existence: `navigator.permissions.query` exists — NOT whether it works for camera/mic. */
  permissionsQueryPresent: boolean;
  /** existence: `HTMLMediaElement.setSinkId` — can we choose an output device? (false on iOS). */
  outputSelectable: boolean;
  /**
   * version: iOS and iPadOS need a user gesture to open the `getUserMedia` prompt. Safari on macOS
   * does not (manual check, 2026-10-09; see BEHAVIOR_FIXTURES).
   */
  requiresUserGesture: boolean;
  /**
   * version: a plain Block is forgotten on reload, so reloading lets the browser ask again. True on
   * Firefox (unless the user ticked "Remember this decision", which the page cannot see) and iOS.
   */
  denialClearsOnReload: boolean;
  /**
   * version: how many closed prompts make the browser stop asking by itself (0 = never). It blocks
   * silently: `permissions.query` still reads 'prompt'.
   */
  dismissalsBeforeAutoBlock: number;
  /** version: Firefox grants are one-time by default; elsewhere persistent. */
  permissionSemantics: 'persistent' | 'one-time';
  /** version: no browser lets us tell an OS-level block from a browser block via a standard API. */
  distinguishesSystemDenial: boolean;
  /**
   * version: can `permissions.query` ever report a *denied* state for camera/microphone?
   *
   * Mobile WebKit cannot — it answers `'prompt'` whether the user has decided or not, so on iOS a
   * post-failure `'prompt'` reading carries NO information and must not be read as "the prompt was
   * dismissed, ask again". False here tells `mapAcquisitionError` to treat a NotAllowedError as the
   * hard block it is (HNS-234: an iOS mic disabled in the Settings app was filed as a dismissal,
   * so the join gate looped on an "Allow" button instead of showing how to re-enable it).
   *
   * Note this is about the *denied* verdict specifically — distinct from `permissionsQueryPresent`
   * (the API exists on WebKit) and from Firefox's throw-for-camera/mic, which `probes.ts` covers.
   */
  reportsPermissionDenial: boolean;
};

/**
 * Injected environment — pure inputs, so `deriveCapabilities` never touches globals and is
 * unit-testable with no DOM. A thin adapter (added in Phase 1) fills these from `navigator`
 * and `react-device-detect`.
 */
export type CapabilityEnv = {
  hasGetUserMedia: boolean;
  hasPermissionsQuery: boolean;
  hasSetSinkId: boolean;
  isSafari: boolean;
  isIOS: boolean;
  isFirefox: boolean;
  /** iPadOS Safari, which reports itself as a Mac (`isIOS` is false) but behaves like iOS. */
  isTouchMac?: boolean;
};

export const deriveCapabilities = (env: CapabilityEnv): Capabilities => ({
  mediaCaptureSupported: env.hasGetUserMedia,
  permissionsQueryPresent: env.hasPermissionsQuery,
  outputSelectable: env.hasSetSinkId,
  requiresUserGesture: env.isIOS || !!env.isTouchMac,
  denialClearsOnReload: env.isFirefox || env.isIOS || !!env.isTouchMac,
  // Chrome and Edge, and Safari on macOS. Firefox has no dismissal (Esc is Block), nor does iOS.
  dismissalsBeforeAutoBlock: env.isFirefox || env.isIOS || env.isTouchMac ? 0 : 3,
  permissionSemantics: env.isFirefox ? 'one-time' : 'persistent',
  distinguishesSystemDenial: false,
  // Only mobile WebKit. Safari on macOS reports a saved "Never allow" as 'denied', and its plain
  // "Don't Allow" is not remembered at all — a 'prompt' after it really means "ask again"
  // (manual check, 2026-10-09; see BEHAVIOR_FIXTURES).
  reportsPermissionDenial: !(env.isIOS || env.isTouchMac),
});
